import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  clearCart,
  resetSubmittingOrder,
  storeSuccessAndBuildOrder,
} from '../../../actions/v3Order';
import { clearCartVoucherItems } from '../../../actions/action_cart_voucher_items';

// This page renders with no navbar for parents who arrive without persisted
// auth (the route is public — ENG-2781), so the card must read as a complete
// confirmation screen on an otherwise empty page.
//
// Spacing lives here as PADDING rather than a margin on the card. The navbar
// is `position: fixed` (modules/v2/navbar.scss) and `#react-component` reserves
// exactly $navbar-height above every page to clear it, so a top margin on the
// card collapses through that wrapper and resolves to max(50px, cardMargin) —
// landing the card flush against the navbar for logged-in parents no matter
// what value it is given. Padding does not collapse, so this gap is measured
// from the bottom of the reserved navbar band and reads identically whether or
// not the navbar actually rendered.
const Page = styled.div`
  box-sizing: border-box;
  padding: 40px 16px 64px;

  @media screen and (min-width: 501px) {
    padding: 72px 24px 96px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
  text-align: center;
  max-width: 460px;
  margin: 0 auto;
  padding: 40px 36px 36px;

  @media screen and (max-width: 500px) {
    padding: 36px 22px 30px;
  }
`;

const CheckCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #22c55e;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
`;

const drawCheck = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const CheckPath = styled.path`
  stroke-dasharray: 24;
  stroke-dashoffset: 24;
  animation: ${drawCheck} 0.5s ease-out 0.25s forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    stroke-dashoffset: 0;
  }
`;

const Title = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px;
`;

const SubText = styled.p`
  font-size: 16px;
  color: #555;
  line-height: 1.55;
  max-width: 46ch;
  margin: 0 auto;
  text-wrap: balance;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #eceef0;
  margin: 24px 0;
`;

const HomeButton = styled(Link)`
  display: inline-block;
  background-color: #479de6;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  padding: 12px 32px;
  text-decoration: none;

  &:hover,
  &:focus {
    background-color: #357abd;
    color: #ffffff;
    text-decoration: none;
  }

  @media screen and (max-width: 500px) {
    display: block;
  }
`;

const MicroText = styled.p`
  font-size: 13.5px;
  color: #8a919c;
  margin: 18px 0 0;
`;

const Checkout = (props) => {
  const dispatch = useDispatch();
  const query = new URLSearchParams(props.location.search);
  const sessionId = query.get('session_id');
  const uuid = query.get('cart_uuid');

  useEffect(() => {
    // clear the cart immediately
    dispatch(clearCart());

    // reset the order-submitting state
    dispatch(resetSubmittingOrder());

    // Payment has already succeeded by the time Stripe redirects here — this
    // call only kicks off the order build so the receipt email goes out right
    // away. If it fails or never returns, the look-for-orders-to-build cron
    // builds the order instead, so the confirmation copy above stays true and
    // nothing on this page should block on it.
    const fetchOrder = async () => {
      await dispatch(storeSuccessAndBuildOrder(sessionId, uuid));

      // Clear voucher items AFTER order is built (voucher data is now stored in cart JSON)
      dispatch(clearCartVoucherItems(uuid));
    };

    fetchOrder();
  }, [sessionId]);

  return (
    <Page>
      <Card>
        <CheckCircle>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <CheckPath d="M5 13l4 4L19 7" />
          </svg>
        </CheckCircle>
        <Title>Order submitted!</Title>
        <SubText>
          Thanks — your payment went through. We&rsquo;ll email your
          confirmation and order details shortly.
        </SubText>
        <Divider />
        <HomeButton to="/dashboard">Back to Home</HomeButton>
        <MicroText>It&rsquo;s safe to close this page.</MicroText>
      </Card>
    </Page>
  );
};

export default Checkout;
